import React, { useState, useCallback, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { useRecords } from './hooks/useRecords';
import { useToast } from './hooks/useToast';
import { useInspection } from './hooks/useInspection';
import { api } from './api/client';
import { TopBar } from './components/layout/TopBar';
import { MobileNav } from './components/layout/MobileNav';
import { PageHeader } from './components/layout/PageHeader';
import { FormWizard } from './pages/FormWizard';
import { RecordView } from './pages/RecordView';
import { MainDashboard } from './components/dashboard/MainDashboard';
import { SectionsDashboard } from './components/dashboard/SectionsDashboard';
import { DashboardTabs } from './components/dashboard/DashboardTabs';
import { ManageChoicesModal } from './components/modals/ManageChoicesModal';
import { NGDefectsModal } from './components/modals/NGDefectsModal';
import { ShareAccessModal } from './components/modals/ShareAccessModal';
import { WhatsAppModal } from './components/modals/WhatsAppModal';
import { InspectorManagerModal } from './components/modals/InspectorManagerModal';
import { LoginPage } from './pages/LoginPage';
import type { Metadata } from '@skd/shared';

export default function App() {
  const { isAuthenticated, isLoading: authLoading, inspector } = useAuth();
  const { records, saveRecord, deleteRecord, refreshRecords } = useRecords(isAuthenticated);
  const { showToast } = useToast();
  const inspection = useInspection();

  const [activeDashTab, setActiveDashTab] = useState<'MAIN' | 'SECTIONS'>('MAIN');
  const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [showChoicesModal, setShowChoicesModal] = useState(false);
  const [showDefectsModal, setShowDefectsModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [showInspectorModal, setShowInspectorModal] = useState(false);
  const [defectRecordId, setDefectRecordId] = useState<string | null>(null);
  const [defects, setDefects] = useState<any[]>([]);

  const [invoiceMappings, setInvoiceMappings] = useState<{ invoiceNo: string; bodies: string[] }[]>([]);
  const [standaloneCases, setStandaloneCases] = useState<string[]>([]);
  const [standaloneBoxes, setStandaloneBoxes] = useState<string[]>([]);

  useEffect(() => {
    if (isAuthenticated) {
      api.getInvoiceMappings()
        .then((data) => setInvoiceMappings(data.mappings))
        .catch((err) => showToast(`Failed to load invoice mappings: ${err.message}`));
      api.getStandaloneChoices()
        .then((data) => {
          setStandaloneCases(data.choices.cases);
          setStandaloneBoxes(data.choices.boxes);
        })
        .catch((err) => showToast(`Failed to load choices: ${err.message}`));
    }
  }, [isAuthenticated, showToast]);

  const handleSaveRecord = useCallback(async (metadata: Metadata, stages: any) => {
    const record = await saveRecord(metadata, stages, inspector?.display_name);
    return record;
  }, [saveRecord, inspector]);

  const handleDeleteRecord = useCallback(async (id: string) => {
    if (window.confirm('Delete this record permanently?')) {
      try {
        await deleteRecord(id);
        showToast('Record deleted.');
        if (selectedRecordId === id) setSelectedRecordId(null);
      } catch (err: any) {
        showToast(`Delete failed: ${err.message}`);
      }
    }
  }, [deleteRecord, selectedRecordId, showToast]);

  const handleOpenDefects = useCallback(async (recordId: string) => {
    setDefectRecordId(recordId);
    try {
      const data = await api.getDefects(recordId);
      setDefects(data.defects);
    } catch (err: any) {
      showToast(`Failed to load defects: ${err.message}`);
      setDefects([]);
    }
    setShowDefectsModal(true);
  }, [showToast]);

  const handleSaveChoices = useCallback(async (
    mappings: { invoiceNo: string; bodies: string[] }[],
    cases: string[],
    boxes: string[]
  ) => {
    try {
      await api.saveInvoiceMappings(mappings);
      await api.saveStandaloneChoices({ cases, boxes });
      setInvoiceMappings(mappings);
      setStandaloneCases(cases);
      setStandaloneBoxes(boxes);
      showToast('Choice options saved!');
    } catch (err: any) {
      showToast(`Failed to save choices: ${err.message}`);
    }
  }, [showToast]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-primary)]">
        <div className="text-[var(--text-muted)]">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  const defectBodyNumber = defectRecordId
    ? records.find((r) => r.id === defectRecordId)?.metadata?.body_number || defectRecordId
    : '';

  return (
    <div className="min-h-screen bg-[var(--bg-primary)]">
      <TopBar
        onOpenShare={() => setShowShareModal(true)}
        onOpenWhatsApp={() => setShowWhatsAppModal(true)}
        onOpenInspectors={() => setShowInspectorModal(true)}
      />

      <main className="mx-auto max-w-[1280px] px-3 pb-24 pt-4 sm:px-5 sm:pb-8 sm:pt-6">
        <Routes>
          <Route path="/" element={<Navigate to="/inspect" replace />} />
          <Route
            path="/inspect"
            element={
              <FormWizard
                inspection={inspection}
                invoiceMappings={invoiceMappings}
                standaloneCases={standaloneCases}
                standaloneBoxes={standaloneBoxes}
                onSaveRecord={handleSaveRecord}
                onOpenChoices={() => setShowChoicesModal(true)}
              />
            }
          />
          <Route
            path="/inspect/section/:step"
            element={
              <FormWizard
                inspection={inspection}
                invoiceMappings={invoiceMappings}
                standaloneCases={standaloneCases}
                standaloneBoxes={standaloneBoxes}
                onSaveRecord={handleSaveRecord}
                onOpenChoices={() => setShowChoicesModal(true)}
              />
            }
          />
          <Route path="/records/:id" element={<RecordViewRoute records={records} />} />
          <Route
            path="/dashboard"
            element={
              <DashboardLayout
                activeDashTab="MAIN"
                setActiveDashTab={setActiveDashTab}
                records={records}
              >
                <MainDashboard
                  records={records}
                  selectedInvoiceNo={inspection.metadata.invoice_no}
                  selectedRecordId={selectedRecordId}
                  onSelectRecord={setSelectedRecordId}
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                  onDeleteRecord={handleDeleteRecord}
                  canDeleteRecords={!!inspector?.is_admin}
                  onOpenDefects={handleOpenDefects}
                />
              </DashboardLayout>
            }
          />
          <Route
            path="/dashboard/sections"
            element={
              <DashboardLayout
                activeDashTab="SECTIONS"
                setActiveDashTab={setActiveDashTab}
                records={records}
              >
                <SectionsDashboard records={records} />
              </DashboardLayout>
            }
          />
          <Route path="*" element={<Navigate to="/inspect" replace />} />
        </Routes>
      </main>

      <MobileNav />

      <ManageChoicesModal
        isOpen={showChoicesModal}
        onClose={() => setShowChoicesModal(false)}
        mappings={invoiceMappings}
        cases={standaloneCases}
        boxes={standaloneBoxes}
        onSave={handleSaveChoices}
      />

      <NGDefectsModal
        isOpen={showDefectsModal}
        onClose={() => setShowDefectsModal(false)}
        defects={defects}
        bodyNumber={defectBodyNumber}
      />

      <ShareAccessModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
      />

      <WhatsAppModal
        isOpen={showWhatsAppModal}
        onClose={() => setShowWhatsAppModal(false)}
        records={records}
      />

      <InspectorManagerModal
        isOpen={showInspectorModal}
        onClose={() => setShowInspectorModal(false)}
      />
    </div>
  );
}

function DashboardLayout({
  activeDashTab,
  setActiveDashTab,
  records,
  children,
}: {
  activeDashTab: 'MAIN' | 'SECTIONS';
  setActiveDashTab: (tab: 'MAIN' | 'SECTIONS') => void;
  records: any[];
  children: React.ReactNode;
}) {
  const navigate = useNavigate();

  const handleTabChange = (tab: 'MAIN' | 'SECTIONS') => {
    setActiveDashTab(tab);
    navigate(tab === 'MAIN' ? '/dashboard' : '/dashboard/sections');
  };

  const defectCount = records.reduce((sum, r) => sum + (r.defectCount || 0), 0);

  return (
    <div>
      <PageHeader
        title="Quality Dashboard"
        meta={
          <>
            <span>{records.length} {records.length === 1 ? 'body' : 'bodies'}</span>
            <span className="text-[var(--text-dim)]">·</span>
            <span>{defectCount} open {defectCount === 1 ? 'defect' : 'defects'}</span>
          </>
        }
        actions={<DashboardTabs activeTab={activeDashTab} onChange={handleTabChange} />}
      />
      {children}
    </div>
  );
}

function RecordViewRoute({ records }: { records: any[] }) {
  const { id } = useParams();
  const record = records.find((r) => r.id === id) || null;
  return <RecordView record={record} />;
}
